/* eslint-disable i18next/no-literal-string */
// @noflow
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import BREAKPOINTS from '@/constants/Breakpoints'

import useWindowSize from '@/hooks/useWindowSize'

import { Button } from '@/components/elements/atoms/Button'
import Pill from '@/components/elements/atoms/Pill/Pill'
import Image from '@/components/shared/elements/Image/Image'
import { formattedDate } from '@/components/shared/elements/TestimonialsTrustpilot/helpers'
import Text from '@/components/shared/elements/Text/Text'

import STYLES from './ComparisonTable.module.sass'

import { Code } from '@/shared_types/rails_models/shipping_countries'

import { columns, data, displayedColumnsId, reviews } from './helpers'

type TabName = 'all' | 'column_2' | 'column_3' | 'column_4'
type ColumnId = 'column_0' | 'column_1' | 'column_2' | 'column_3' | 'column_4'

type Props = {
  shippingCountryCode: Code
  isPrewizardGuest: boolean
  screenIdentifier: string
  isUserSignedIn: boolean
}

const ComparisonTable = ({
  shippingCountryCode,
  isPrewizardGuest,
  screenIdentifier,
  isUserSignedIn
}: Props): React.ReactElement => {
  const namespace = 'paid_traffic'
  const { t } = useTranslation(namespace)

  const copyContext = 'phase_3.comparison_table'
  const { windowWidth } = useWindowSize()
  const isMobile = windowWidth <= BREAKPOINTS.sm

  const tabs: TabName[] = ['column_2', 'column_3', 'column_4']
  const [selectedTab, setSelectedTab] = useState<TabName>(
    isMobile ? 'column_2' : 'all'
  )

  const handleChange = useCallback(
    (e) => setSelectedTab(e.target.value),
    [setSelectedTab]
  )

  const tabOptions = tabs.map((tabName) => ({
    id: tabName,
    name: tabName,
    text: `${copyContext}.table.columns.${tabName}.title`,
    value: tabName,
    checked: selectedTab === tabName,
    namespace,
    onChange: handleChange
  }))

  const linkCTADestination = useCallback(() => {
    const ctaDestination = isPrewizardGuest ? '/wizard/new' : '/plans/recipes'
    window.location.href = ctaDestination
  }, [isPrewizardGuest])

  return (
    <div className={`${STYLES.comparisonTableWrapper}`}>
      <div className={`${STYLES.header}`}>
        <Text
          variant="display28"
          bold
          namespace={namespace}
          text={`${copyContext}.title`}
          element="span"
        />
      </div>
      {isMobile && (
        <div className={`${STYLES.mobileTabs}`}>
          <Text
            variant="textRegular20"
            namespace={namespace}
            text={`${copyContext}.subtitle`}
            bold
          />
          <div className={`${STYLES.pillsWrapper}`}>
            {tabOptions.map((tabOption) => (
              <Pill key={tabOption.id} {...tabOption} />
            ))}
          </div>
        </div>
      )}
      <table>
        <thead>
          <tr>
            {columns
              .filter((col) => displayedColumnsId(selectedTab).includes(col.id))
              .map((col) => (
                <th
                  key={col.id}
                  className={
                    col.id === 'column_1'
                      ? `${STYLES.column1Header}`
                      : `${STYLES.otherColumnHeader}`
                  }
                >
                  <div className={`${STYLES.columnHeader}`}>
                    {col.asset && (
                      <Image
                        alt={`${copyContext}.${col.label}.alt`}
                        slug={col.asset}
                        image={{
                          height: 56,
                          width: 56,
                          resizeMode: 'resize_to_fit',
                          quality: 'auto:best',
                          retina: true
                        }}
                      />
                    )}
                    {col.label && (
                      <Text
                        variant="textRegular16"
                        namespace={namespace}
                        text={`${copyContext}.${col.label}.title`}
                        bold
                      />
                    )}
                  </div>
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {data.map((rowData, rowIndex) => (
            <tr
              key={rowData.feature?.text ?? rowIndex}
              className={
                rowIndex === 0
                  ? `${STYLES.firstRow}`
                  : rowIndex === data.length - 1
                  ? `${STYLES.lastRow}`
                  : undefined
              }
            >
              {displayedColumnsId(selectedTab).map((colId) => {
                if (colId === 'column_0') {
                  return (
                    <td key={colId}>
                      <div className={`${STYLES.firstRowHeader}`}>
                        <Image
                          alt=""
                          slug={rowData.feature.icon}
                          image={{
                            height: 24,
                            width: 24,
                            resizeMode: 'resize_to_fit',
                            quality: 'auto:best',
                            retina: true
                          }}
                        />
                        <Text
                          variant="textRegular14"
                          namespace={namespace}
                          text={`${copyContext}.${rowData.feature.text}`}
                          align="left"
                        />
                      </div>
                    </td>
                  )
                } else {
                  return (
                    <td
                      key={colId}
                      className={
                        colId === 'column_1'
                          ? `${STYLES.column1Row}`
                          : `${STYLES.rows}`
                      }
                    >
                      <Image
                        alt={t(`${copyContext}.${rowData[colId]}`)}
                        slug={
                          rowData[colId]
                            ? 'phase-3-green-tick'
                            : 'phase-3-red-cross'
                        }
                        image={{
                          height: 28,
                          width: 28,
                          resizeMode: 'resize_to_fit',
                          quality: 'auto:best',
                          retina: true
                        }}
                      />
                    </td>
                  )
                }
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className={`${STYLES.contentWrapperButton}`}>
        <div className={`${STYLES.contentWrapper}`}>
          {reviews(selectedTab).map((content) => (
            <div className={`${STYLES.cardContent}`} key={content.name}>
              <div className={`${STYLES.cardTitle}`}>
                <Text
                  element="span"
                  variant={isMobile ? 'textRegular20' : 'textRegular16'}
                  namespace={namespace}
                  text={`${copyContext}.reviews.${content.copy}.title`}
                  bold
                />
              </div>
              <Text
                variant="textRegular16"
                namespace={namespace}
                text={`${copyContext}.reviews.${content.copy}.quote`}
              />
              <Image
                alt={t(`phase_2.testimonials.categories.five_stars_alt`)}
                slug="five-stars"
                image={{
                  height: 17,
                  width: 96,
                  resizeMode: 'resize_to_fit',
                  quality: 'auto:best',
                  retina: true
                }}
              />
              <a href={content.url} target="_blank" rel="noreferrer">
                <Text
                  variant="textRegular14"
                  namespace={namespace}
                  variables={{
                    date: formattedDate('2025-06', shippingCountryCode)
                  }}
                  colour="brandBlue400"
                  text="phase_2.testimonials.categories.card_footer"
                />
              </a>
            </div>
          ))}
        </div>
        {!isUserSignedIn && (
          <Button
            typography={{
              namespace,
              text: `${copyContext}.cta`
            }}
            size="regular"
            onClick={linkCTADestination}
            identifier="comparison_table_cta_button"
            screenIdentifier={screenIdentifier}
          />
        )}
      </div>
    </div>
  )
}

export default ComparisonTable
export type { TabName, ColumnId }
